import styled from "styled-components";
import { Grid, Stack, Box, Avatar, Typography, useTheme, useMediaQuery, Skeleton } from "@mui/material";
import { CardStatus } from "../../components/Card/CardStatus";
import { CardContainerRetro } from "../../components/Container/CardContainerRetro";
import { SectionTitleWithExp } from "../../components/customTypo/SectionTitleWithExp";
import { Section } from "../../components/Container/Section";
import { useTranslation } from "react-i18next";
import { IconThreads } from "../../assets/icon/metric/IconThreads";
import moment from "moment";
import { usePerformanceSummaryData } from "../../hooks/useThreadsSelector";
import { DataLoadError } from "../../components/error/DataLoadError";

import colorsCustom from "../../designTokens/colorsCustom";

const StyledGridItem = styled(Box)`
  padding: 0 16px;
  display: flex;
  align-items: stretch;
  min-height: 100%;
`;


export const LifeTimePerfromanceSummarySection = ({
  profile,
  firstThreadDate,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  // 반응형 Hook
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));

  // ThreadsContext에서 성과 요약 데이터 가져오기
  const {
    lifetimeInsight: insight,
    isInsightsLoading,
    isDataReady,
    error,
  } = usePerformanceSummaryData();

  // 로딩 중인 경우 스켈레톤 상태로 렌더링
  const isLoading = isInsightsLoading || !isDataReady;

  // 데이터 로딩 실패
  if (error && !insight) {
    return (
      <Section>
        <SectionTitleWithExp
          title={t("lifetime.keyPerformance.title")}
        />
        <CardContainerRetro
          p={isMobile ? "24px" : "32px"}
          bg={colorsCustom.paper}
          weight="bold"
          rd={16}
        >
          <DataLoadError
            title={t('error.performanceDataFailed')}
            message={t('error.threadsDataLoadFailed')}
            showRetry={false}
          />
        </CardContainerRetro>
      </Section>
    );
  }
  return (
    <Section>
      <SectionTitleWithExp
        title={t("lifetime.keyPerformance.title")}
        // exp={t("lifetime.keyPerformance.description")}
      />
      <CardContainerRetro
        bg={colorsCustom.paper}
        w={"100%"}
        weight="bold"
        rd={16}
      >
        <Stack
          sx={{
            width: "100%",
            p: isMobile ? "16px 0px 0px 0px" : "24px 8px",
            gap: 3,
          }}
        >
          <Grid
            container
            spacing={isMobile ? 2 : 0}
            sx={{
              width: "100%",
              height: isMobile ? "auto" : 109,
              alignItems: "stretch",
              minHeight: "100%",
            }}
          >
            {/* 프로필 섹션 */}
            <Grid size={{ xs: 12, xl: 4 }}>
              <StyledGridItem>
                <Stack 
                  direction="row" 
                  spacing={3} 
                  sx={{
                    borderRight: !isMobile ? `0.5px solid ${colorsCustom.ui.dark.subtle}` : "none",
                    marginBottom: isMobile ? 2 : 0,
                    alignItems: "flex-start",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: "fit-content",
                      height: "fit-content",
                      borderRadius: "50%",
                      padding: 1,
                      border: `4px solid ${colorsCustom.ui.dark.bold}`,
                    }}
                  >
                    {isLoading ? (
                      <Skeleton 
                        variant="circular" 
                        width={isMobile ? 56 : 72} 
                        height={isMobile ? 56 : 72} 
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: isMobile ? 56 : 72,
                          height: isMobile ? 56 : 72,
                        }}
                        src={profile?.threads_profile_picture_url}
                      />
                    )}
                  </Box>
                  <Stack spacing={2.5}>
                    <Stack spacing={0}>
                      {isLoading ? (
                        <>
                          <Skeleton 
                            variant="text" 
                            width={120} 
                            height={isMobile ? 28 : 32} 
                            sx={{ mb: isMobile ? 1 : 2 }} 
                          />
                          <Skeleton 
                            variant="text" 
                            width={100} 
                            height={isMobile ? 20 : 24} 
                            sx={{ mb: 1 }} 
                          />
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Skeleton variant="rectangular" width={16} height={16} />
                            <Skeleton variant="text" width={80} height={isMobile ? 20 : 24} />
                          </Stack>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant={isMobile ? "h7" : "h6"}
                            fontWeight={700}
                            sx={{ mb: isMobile ? 1 : 2 }}
                          >
                            {profile?.username ?? "-"}
                          </Typography>
                          <Typography
                            variant={isMobile ? "body2" : "body1"}
                            color="text.secondary"
                          >
                            {firstThreadDate
                              ? moment(firstThreadDate).format("MMM D, YYYY")
                              : "-"}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconThreads size={16} />
                            <Typography
                              variant={isMobile ? "body2" : "body1"}
                              color="text.secondary"
                            >
                              {`${insight?.numThreads ?? 0} threads`}
                            </Typography>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </StyledGridItem>
            </Grid>

            {/* 메트릭 섹션들 */}
            <Grid size={{ xs: 6, xl: 2 }}>
              <StyledGridItem>
                <Box sx={{
                  borderRight: !isMobile ? `0.5px solid ${colorsCustom.ui.dark.subtle}` : "none",
                  borderBottom: isMobile ? `0.5px solid ${colorsCustom.ui.dark.subtle}` : "none",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isMobile ? "16px" : "0",
                }}>
                  <CardStatus
                    type={"follower"}
                    value={insight?.followers?.value ?? 0}
                    postLoading={false}
                    isLoading={isLoading}
                  />
                </Box>
              </StyledGridItem>
            </Grid>

            <Grid size={{ xs: 6, xl: 2 }}>
              <StyledGridItem>
                <Box sx={{
                  borderRight: !isMobile ? `0.5px solid ${colorsCustom.ui.dark.subtle}` : "none",
                  borderBottom: isMobile ? `0.5px solid ${colorsCustom.ui.dark.subtle}` : "none",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isMobile ? "16px" : "0",
                }}>
                  <CardStatus
                    type={"like"}
                    value={insight?.likes?.value ?? 0}
                    postLoading={false}
                    isLoading={isLoading}
                  />
                </Box>
              </StyledGridItem>
            </Grid>

            <Grid size={{ xs: 6, xl: 2 }}>
              <StyledGridItem>
                <Box sx={{
                  borderRight: !isMobile ? `0.5px solid ${colorsCustom.ui.dark.subtle}` : "none",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isMobile ? "16px" : "0",
                }}>
                  <CardStatus
                    type={"reply"}
                    value={insight?.replies?.value ?? 0}
                    postLoading={false}
                    isLoading={isLoading}
                  />
                </Box>
              </StyledGridItem>
            </Grid>

            <Grid size={{ xs: 6, xl: 2 }}>
              <StyledGridItem>
                <Box sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isMobile ? "16px" : "0",
                }}>
                  <CardStatus
                    type={"repost"}
                    value={insight?.reposts?.value ?? 0}
                    postLoading={false}
                    isLoading={isLoading}
                  />
                </Box>
              </StyledGridItem>
            </Grid>
          </Grid>
        </Stack>
      </CardContainerRetro>
    </Section>
  );
};
